import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { isCurrentUserAdmin } from '../utils/auth';
import { apiFetch } from '../utils/api';
import './BottleDetailScreen.css';

interface Wine {
  id: number; name: string; producer: string | null; appellation: string | null;
  region: string | null; country: string | null; vintage: number | null;
  type: string | null; color: string | null; grapes: string | null;
  alcohol: number | null; volume_ml: number | null; tasting_notes: string | null;
  food_pairings: string | null; avg_price_eur: number | null; photo_url: string | null;
  ean: string | null;
}

function colorInfo(color?: string | null, type?: string | null) {
  const raw = (color || type || '').toLowerCase();
  if (raw.includes('rouge') || raw.includes('red')) return { label: 'Rouge', cls: 'rouge' };
  if (raw.includes('blanc') || raw.includes('white')) return { label: 'Blanc', cls: 'blanc' };
  if (raw.includes('ros')) return { label: 'Rosé', cls: 'rose' };
  if (raw.startsWith('spirit:')) return { label: raw.replace('spirit:', '').charAt(0).toUpperCase() + raw.slice(8), cls: 'spirit' };
  return { label: color || type || '', cls: 'other' };
}

function parseJson(s: string | null): string[] {
  if (!s) return [];
  try { return JSON.parse(s); } catch { return []; }
}

export function BottleDetailScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [wine, setWine] = useState<Wine | null>(null);
  const [loading, setLoading] = useState(true);
  const [eanInput, setEanInput] = useState('');
  const [showEanForm, setShowEanForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const admin = isCurrentUserAdmin();

  const saveEan = async (ean: string) => {
    // `auth: false` retiré : PATCH /cave/wines/:id/ean exige désormais un admin
    // côté backend. Le formulaire est déjà derrière `isCurrentUserAdmin()`, donc
    // l'appelant a un token — il n'était simplement pas envoyé.
    const res = await apiFetch(`/cave/wines/${wine?.id}/ean`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ean }),
    });
    if (res.ok && wine) {
      setWine({ ...wine, ean });
      setShowEanForm(false);
      setShowScanner(false);
      setEanInput('');
    } else {
      const body = await res.text().catch(() => '');
      alert('Erreur: ' + (body || res.status));
    }
  };

  useEffect(() => {
    apiFetch(`/cave/wines/${id}`, { auth: false })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { setWine(data?.wine ?? null); setLoading(false); })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="bd-page bd-center"><p>Chargement...</p></div>;
  if (!wine) return <div className="bd-page bd-center"><p>Vin non trouve</p><button onClick={() => navigate(-1)}>Retour</button></div>;

  const ci = colorInfo(wine.color, wine.type);
  const grapes = parseJson(wine.grapes);
  const pairings = parseJson(wine.food_pairings);
  const displayName = [wine.name, wine.vintage].filter(Boolean).join(' ');

  return (
    <div className="bd-page">
      <div className="bd-header">
        <button onClick={() => navigate(-1)}>‹</button>
        <span>Fiche</span>
        <div style={{ width: 24 }} />
      </div>

      {/* Hero */}
      <div className="bd-hero">
        <div className={`bd-visual ${ci.cls}`}>
          {wine.photo_url ? <img src={wine.photo_url} alt={wine.name} /> : <span className="bd-icon">🍷</span>}
        </div>
        <span className={`bd-color-badge ${ci.cls}`}>{ci.label}</span>
        <h1>{displayName}</h1>
        {wine.producer && <p className="bd-producer">{wine.producer}</p>}

        <div className="bd-key-info">
          {wine.appellation && <div><span>Appellation</span><strong>{wine.appellation}</strong></div>}
          {wine.region && <div><span>Region</span><strong>{wine.region}</strong></div>}
          {wine.country && <div><span>Pays</span><strong>{wine.country}</strong></div>}
        </div>
      </div>

      {/* Stats */}
      {(wine.alcohol || wine.volume_ml || wine.avg_price_eur) && (
        <div className="bd-section">
          <h2>Caracteristiques</h2>
          <div className="bd-stats">
            {wine.alcohol && <div className="bd-stat"><span className="bd-stat-val">{wine.alcohol}%</span><span className="bd-stat-lbl">Alcool</span></div>}
            {wine.volume_ml && <div className="bd-stat"><span className="bd-stat-val">{wine.volume_ml >= 1000 ? `${(wine.volume_ml / 1000).toFixed(1)}L` : `${wine.volume_ml}ml`}</span><span className="bd-stat-lbl">Volume</span></div>}
            {wine.avg_price_eur && <div className="bd-stat"><span className="bd-stat-val">{wine.avg_price_eur}€</span><span className="bd-stat-lbl">Prix moy.</span></div>}
          </div>
        </div>
      )}

      {/* Cepages */}
      {grapes.length > 0 && (
        <div className="bd-section">
          <h2>Cepages</h2>
          <div className="bd-tags">
            {grapes.map((g, i) => <span key={i} className={`bd-tag ${ci.cls}`}>{g}</span>)}
          </div>
        </div>
      )}

      {/* Degustation */}
      {wine.tasting_notes && (
        <div className="bd-section">
          <h2>Notes de degustation</h2>
          <p className="bd-body">{wine.tasting_notes}</p>
        </div>
      )}

      {/* Accords */}
      {pairings.length > 0 && (
        <div className="bd-section">
          <h2>Accords mets & vins</h2>
          <div className="bd-tags">
            {pairings.map((p, i) => <span key={i} className="bd-tag neutral">{p}</span>)}
          </div>
        </div>
      )}

      {/* Code-barres */}
      <div className="bd-section">
        <h2>Code-barres</h2>
        {wine.ean ? (
          <div className="bd-barcode">
            <div className="bd-ean-display">
              <span className="bd-ean-code">{wine.ean}</span>
              <span className="bd-ean-label">EAN-13</span>
            </div>
          </div>
        ) : (
          <div className="bd-no-barcode">
            <p>Aucun code-barres associe</p>
            {admin && !showEanForm && (
              <div className="bd-ean-actions">
                <button className="bd-scan-ean" onClick={() => setShowScanner(true)}>
                  📷 Scanner
                </button>
                <button className="bd-add-ean" onClick={() => setShowEanForm(true)}>
                  ⌨️ Saisir
                </button>
              </div>
            )}
            {admin && showEanForm && (
              <div className="bd-ean-form">
                <input
                  value={eanInput}
                  onChange={(e) => setEanInput(e.target.value)}
                  placeholder="Code EAN (ex: 3760195880210)"
                  type="text"
                  inputMode="numeric"
                  autoFocus
                />
                <button className="bd-ean-save" disabled={!eanInput.trim()} onClick={() => saveEan(eanInput.trim())}>
                  OK
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner
          onScan={(code) => saveEan(code)}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}
